import type { AxiosProgressEvent } from "axios";
import { BaseModule } from "./baseModule";
import type {
  ConversationMessage,
  ConversationWithMetadata,
  EnhancedConversationMessage,
  MessageSearchResult,
  MessageSearchRequest,
  ConversationInput,
} from "./types";

/**
 * MessagingModule handles all messaging and conversation operations
 * Supports user-admin communication with attachments and search
 */
export class MessagingModule extends BaseModule {
  // ============================================================================
  // Core Messaging Features (Task 7.1)
  // ============================================================================

  /**
   * Get all conversations for the current user
   * @returns List of conversation messages
   */
  async getConversations(): Promise<EnhancedConversationMessage[]> {
    const response = await this.get<{
      messages?: EnhancedConversationMessage[];
      total?: number;
    }>("/api/conversations");
    return response.messages ?? [];
  }

  /**
   * Send a message in a conversation
   * @param data Message content
   * @returns Created message
   */
  async sendMessage(data: ConversationInput): Promise<ConversationMessage> {
    return this.post<ConversationMessage>("/api/conversations", data);
  }

  /**
   * Get conversation details with metadata
   * @param id Conversation ID (user_id)
   * @returns Conversation with metadata including participants and unread count
   */
  async getConversationDetails(id: string): Promise<ConversationWithMetadata> {
    const messages = await this.getAdminConversations(id);
    return {
      id,
      participants: [],
      last_message: messages[messages.length - 1],
      unread_count: messages.filter((message) => !message.is_read).length,
      created_at: messages[0]?.created_at ?? new Date().toISOString(),
      updated_at:
        messages[messages.length - 1]?.created_at ?? new Date().toISOString(),
    };
  }

  /**
   * Get messages from a specific conversation with pagination
   * @param id Conversation ID (user_id)
   * @param page Page number (default: 1)
   * @param limit Messages per page (default: 50)
   * @returns List of enhanced messages with attachments
   */
  async getConversationMessages(
    id: string,
    page?: number,
    limit?: number,
  ): Promise<EnhancedConversationMessage[]> {
    const messages = await this.getAdminConversations(id);
    if (!page || !limit) return messages;

    const start = (page - 1) * limit;
    return messages.slice(start, start + limit);
  }

  /**
   * Mark a specific message as read
   * @param id Message ID
   */
  async markMessageRead(_id: string): Promise<void> {
    await this.markConversationRead();
  }

  /**
   * Mark all messages in a conversation as read (for users)
   * This marks all admin messages as read for the current user
   */
  async markConversationRead(): Promise<{
    message: string;
    messages_marked: number;
  }> {
    return this.post<{ message: string; messages_marked: number }>(
      "/api/conversations/read",
      {},
    );
  }

  // ============================================================================
  // Enhanced Messaging Capabilities (Task 7.2)
  // ============================================================================

  /**
   * Search messages with query parameters
   * @param request Search parameters including query, conversation_id, pagination
   * @returns Search results with messages and metadata
   */
  async searchMessages(
    request: MessageSearchRequest,
  ): Promise<MessageSearchResult> {
    const query = request.query.trim().toLowerCase();
    const messages = await this.getConversations();
    const filteredMessages = messages.filter((message) =>
      message.message?.toLowerCase().includes(query),
    );
    const offset = request.offset ?? 0;
    const limit = request.limit ?? filteredMessages.length;

    return {
      messages: filteredMessages.slice(offset, offset + limit),
      total_count: filteredMessages.length,
      has_more: offset + limit < filteredMessages.length,
    };
  }

  /**
   * Send a message with optional file attachments
   * @param data Message data including content and optional attachments
   * @param onProgress Optional progress callback for upload tracking
   * @returns Created message with attachments
   */
  async sendMessageWithAttachments(
    data: {
      message: string;
      recipient_id?: string;
      conversation_id?: string;
      attachments?: File[];
    },
    onProgress?: (progress: number) => void,
  ): Promise<EnhancedConversationMessage> {
    const formData = new FormData();
    formData.append("message", data.message);

    if (data.recipient_id) {
      formData.append("recipient_id", data.recipient_id);
    }
    if (data.conversation_id) {
      formData.append("conversation_id", data.conversation_id);
    }

    // Add attachments if provided
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file) => {
        formData.append("attachments", file);
      });
    }

    const config: import("axios").AxiosRequestConfig = {};

    if (onProgress) {
      config.onUploadProgress = (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(progress);
        }
      };
    }

    if (data.attachments?.length) {
      throw new Error(
        "REST attachment uploads are not supported by the backend. Use the WebSocket attachment flow.",
      );
    }

    void formData;
    void config;
    return this.post<EnhancedConversationMessage>("/api/messages", {
      message: data.message,
    });
  }

  /**
   * Download a message attachment
   * @param filename Attachment filename
   * @returns Blob containing the file data
   */
  async getAttachment(filename: string): Promise<Blob> {
    return this.get<Blob>(`/api/attachments/${filename}`, {
      responseType: "blob",
    });
  }

  // ============================================================================
  // Admin Messaging Features (Task 7.3)
  // ============================================================================

  /**
   * Get conversations for a specific user (admin only)
   * @param userId User ID to get conversations for
   * @returns List of conversation messages
   */
  async getAdminConversations(
    userId: string,
  ): Promise<EnhancedConversationMessage[]> {
    return this.get<EnhancedConversationMessage[]>(
      `/api/admin/conversations/${userId}`,
    );
  }

  /**
   * Send a message as admin to a specific user
   * @param userId Target user ID
   * @param data Message content
   * @returns Created message
   */
  async sendAdminMessage(
    userId: string,
    data: ConversationInput,
  ): Promise<ConversationMessage> {
    return this.post<ConversationMessage>(
      `/api/admin/conversations/${userId}`,
      data,
    );
  }

  /**
   * Mark all messages in a conversation as read (admin only)
   * @param userId User ID of the conversation
   * @returns Result with count of messages marked
   */
  async markAdminConversationRead(
    userId: string,
  ): Promise<{ message: string; messages_marked: number }> {
    return this.post<{ message: string; messages_marked: number }>(
      `/api/admin/conversations/${userId}/read`,
      {},
    );
  }
}
